import { Box, CircularProgress } from "@mui/material";
import { useQuery } from "react-query";
import { getStyles } from "src/styles/theme";
import { getMointoringMemberHistory } from "src/utils/api/dashboard";
import defaultStyles, { StylesClasses } from "./styles";

const FIELD_LABELS = {
  name: "Name of member",
  designation: "Designation",
  phoneNumber: "Phone Number",
  email: "Email",
};

const SUB_TYPE_LABELS = {
  IMPLEMENTING_OFFICER: "Implementing Officer",
  ENTITY: "Entity",
  NODAL_OFFICER: "Nodal Officer",
  ADMIN: "Admin",
};

const formatTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const describeActor = (entry) => {
  const role =
    SUB_TYPE_LABELS[entry.changedBySubType] ?? entry.changedBySubType;

  if (entry.changedByName && role) return `${entry.changedByName} (${role})`;
  if (entry.changedByName) return entry.changedByName;
  if (role) return role;
  // Members added before change tracking existed have no recorded author.
  return null;
};

export function MonitoringMemberHistory({ member }: any) {
  const styles = getStyles<StylesClasses>(defaultStyles);

  const { data, isLoading } = useQuery(
    ["memberHistory", member?.id],
    () => getMointoringMemberHistory(member.id),
    {
      enabled: !!member?.id,
      refetchOnMount: "always",
    }
  );

  const history = data?.data?.data ?? [];

  return (
    <Box {...styles("wrapper")}>
      <Box {...styles("heading")}>Update History</Box>
      <Box {...styles("subHeading")}>
        {member?.name ? `Committee member: ${member.name}` : ""}
      </Box>

      {isLoading && (
        <Box {...styles("emptyState")}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!isLoading && history.length === 0 && (
        <Box {...styles("emptyState")}>
          No changes have been recorded for this member yet.
        </Box>
      )}

      {!isLoading && history.length > 0 && (
        <Box {...styles("entryList")}>
          {history.map((entry) => (
            <Box key={entry.id} {...styles("entry")}>
              <Box {...styles("entryHeader")}>
                <Box {...styles("action")}>
                  {entry.action === "CREATE" ? "Added" : "Updated"}
                </Box>
                <Box {...styles("timestamp")}>
                  {formatTimestamp(entry.changedAt)}
                </Box>
              </Box>

              <Box {...styles("actor")}>
                {describeActor(entry)
                  ? `By ${describeActor(entry)}`
                  : "Author not recorded — predates change tracking"}
              </Box>

              {entry.action === "CREATE" ? (
                <>
                  {Object.keys(FIELD_LABELS).map((field) => (
                    <Box key={field} {...styles("changeRow")}>
                      <Box {...styles("changeLabel")}>
                        {FIELD_LABELS[field]}
                      </Box>
                      <Box {...styles("newValue")}>
                        {entry.snapshot?.[field] || "—"}
                      </Box>
                    </Box>
                  ))}
                </>
              ) : (
                <>
                  {entry.changes?.map((change) => (
                    <Box key={change.field} {...styles("changeRow")}>
                      <Box {...styles("changeLabel")}>
                        {FIELD_LABELS[change.field] ?? change.field}
                      </Box>
                      <Box {...styles("oldValue")}>
                        {change.oldValue || "—"}
                      </Box>
                      <Box {...styles("arrow")}>→</Box>
                      <Box {...styles("newValue")}>
                        {change.newValue || "—"}
                      </Box>
                    </Box>
                  ))}
                </>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
